import React from 'react'
import { Dropdown, FormControl } from 'react-bootstrap'

export interface ColumnsPickerButtonProps {
  keys: string[]
  names: Record<string, string>
  allKeys: string[]
  columnConfigs: Record<string, { invisible?: boolean; order?: number; sort?: string }>
  configKey: string
  onColumnsChange: (config: { columns: Record<string, Record<string, { invisible?: boolean; order?: number; sort?: string }>> }) => void
}

/**
 * Always-visible "Columns" button above the table. Opens the same add-column picker so users
 * don't need to scroll right to add columns. Only show when there are hidden columns.
 */
export function ColumnsPickerButton(props: ColumnsPickerButtonProps) {
  const { keys, names, allKeys, columnConfigs, configKey, onColumnsChange } = props
  const [show, setShow] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState('')
  const hiddenKeys = allKeys.filter((k) => !keys.includes(k))
  const filteredHiddenKeys = hiddenKeys.filter((k) =>
    (names[k] || k).toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (hiddenKeys.length === 0) return null

  const handleAdd = (key: string) => {
    const visibleKeys = keys
    const maxOrder = Math.max(-1, ...visibleKeys.map((k) => (columnConfigs[k]?.order ?? 0)))
    onColumnsChange({
      columns: {
        [configKey]: {
          [key]: { ...columnConfigs[key], invisible: false, order: maxOrder + 1 },
        },
      },
    })
    setShow(false)
    setSearchTerm('')
  }

  return (
    <div className="columns-picker-toolbar">
      <Dropdown show={show} onToggle={(isOpen) => { setShow(isOpen); if (!isOpen) setSearchTerm(''); }}>
        <Dropdown.Toggle as="button" className="columns-picker-btn" type="button">
          <span className="fa fa-plus" />
          Add column
        </Dropdown.Toggle>
        <Dropdown.Menu className="add-metric-dropdown-menu" align="end">
          <div style={{ padding: '8px 12px' }}>
            <FormControl
              type="text"
              placeholder="Search columns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {filteredHiddenKeys.length > 0 ? (
              filteredHiddenKeys.map((k) => (
                <Dropdown.Item key={k} onClick={() => handleAdd(k)}>
                  {names[k] ?? k}
                </Dropdown.Item>
              ))
            ) : (
              <Dropdown.Item disabled>No columns found</Dropdown.Item>
            )}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  )
}

export interface EditableTableHeaderProps {
  keys: string[]
  names: Record<string, string>
  allKeys: string[]
  columnConfigs: Record<string, { invisible?: boolean; order?: number; sort?: string }>
  configKey: string
  onColumnsChange: (config: { columns: Record<string, Record<string, { invisible?: boolean; order?: number; sort?: string }>> }) => void
  sortableFields?: string[]
  sortBy?: string
  sortAsc?: boolean
  onSort?: (sortBy: string, sortAsc: boolean) => void
}

interface EditableTableHeaderState {
  draggedEntryIndex: number | null
  draggedOverEntryIndex: number | null
}

/**
 * Table header for editable columns: draggable column headers and remove button per column.
 * Add column is handled by ColumnsPickerButton above the table. Caller should use
 * effectiveColumnConfigs (merged saved + pending) so order/visibility updates are reflected.
 */
class EditableTableHeader extends React.Component<EditableTableHeaderProps, EditableTableHeaderState> {
  state: EditableTableHeaderState = {
    draggedEntryIndex: null,
    draggedOverEntryIndex: null,
  }

  reorder = (nextVisibleKeys: string[]) => {
    const { configKey, columnConfigs, onColumnsChange } = this.props
    const partial: Record<string, { invisible?: boolean; order?: number; sort?: string }> = {}
    nextVisibleKeys.forEach((k, i) => {
      partial[k] = { ...columnConfigs[k], order: i, invisible: false }
    })
    onColumnsChange({ columns: { [configKey]: partial } })
    this.setState({ draggedEntryIndex: null, draggedOverEntryIndex: null })
  }

  handleRemove = (key: string) => {
    const { configKey, columnConfigs, onColumnsChange } = this.props
    onColumnsChange({
      columns: {
        [configKey]: {
          [key]: { ...columnConfigs[key], invisible: true },
        },
      },
    })
  }

  render() {
    const {
      keys,
      names,
      columnConfigs,
      sortableFields = [],
      sortBy,
      sortAsc,
      onSort,
    } = this.props
    const { draggedEntryIndex, draggedOverEntryIndex } = this.state

    return (
      <thead>
        <tr>
          {keys.map((key, index) => {
            const isDraggedOver = draggedOverEntryIndex === index
            const isDragging = draggedEntryIndex === index
            const isDraggingDown = draggedEntryIndex !== null && draggedEntryIndex < (draggedOverEntryIndex ?? 0)
            const isDraggingUp = draggedEntryIndex !== null && draggedEntryIndex > (draggedOverEntryIndex ?? 0)
            const sortable = sortableFields.indexOf(key) >= 0
            return (
              <th
                key={key}
                className={`
                  ${isDraggedOver ? 'dragged-over-entry' : ''}
                  ${isDragging ? 'dragged-entry' : ''}
                  ${isDraggedOver && isDraggingDown ? 'dragging-down' : ''}
                  ${isDraggedOver && isDraggingUp ? 'dragging-up' : ''}
                `}
                draggable
                onDragOver={(e) => {
                  e.preventDefault()
                  this.setState({ draggedOverEntryIndex: index })
                }}
                onDragStart={() => this.setState({ draggedEntryIndex: index })}
                onDrop={() => {
                  if (draggedEntryIndex !== null && draggedEntryIndex !== index) {
                    const nextKeys = [...keys]
                    const [removed] = nextKeys.splice(draggedEntryIndex, 1)
                    nextKeys.splice(index, 0, removed)
                    this.reorder(nextKeys)
                  }
                }}
                onDragEnd={() => this.setState({ draggedEntryIndex: null, draggedOverEntryIndex: null })}
              >
                <span className="editable-table-header-cell" style={{ cursor: 'default' }}>
                  <span className="fa fa-bars drag-handle" style={{ marginRight: 6, cursor: 'move' }} />
                  <span
                    className={`header-title ${sortable ? 'sortable' : ''}`}
                    onClick={sortable && onSort ? () => onSort(key, sortBy === key ? !sortAsc : true) : undefined}
                    style={
                      sortable
                        ? { cursor: sortBy === key && !sortAsc ? 'n-resize' : 's-resize' }
                        : { cursor: 'default' }
                    }
                  >
                    {names[key] ?? key}
                    {sortable && sortBy === key ? (
                      <span
                        className="sort-indicator"
                        style={{ marginLeft: 4, fontSize: '0.85em', lineHeight: 1 }}
                        aria-hidden
                      >
                        {sortAsc ? '▲' : '▼'}
                      </span>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    className="delete-btn"
                    onClick={(e) => {
                      e.stopPropagation()
                      this.handleRemove(key)
                    }}
                    title="Remove column"
                  >
                    <span className="fa fa-times" />
                  </button>
                </span>
              </th>
            )
          })}
        </tr>
      </thead>
    )
  }
}

export default EditableTableHeader
